package com.dotdrives.client

import com.dotdrives.core.uuidV7
import com.dotdrives.model.AutomationRule
import com.dotdrives.model.NewAutomationRule
import com.dotdrives.model.RulePreviewCount
import com.dotdrives.model.buildRuleInsert
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days

class AutomationRuleClient(
    private val client: SupabaseClient = SupabaseProvider.client,
) {
    suspend fun list(orgId: String): List<AutomationRule> =
        client.from(AUTOMATION_RULES_TABLE)
            .select {
                order("created_at", Order.ASCENDING)
                filter { eq("org_id", orgId) }
            }
            .decodeList()

    suspend fun setEnabled(orgId: String, ruleId: String, enabled: Boolean) {
        client.from(AUTOMATION_RULES_TABLE).update(
            RuleEnabledUpdate(isEnabled = enabled, updatedAt = Clock.System.now().toString()),
        ) {
            filter {
                eq("org_id", orgId)
                eq("id", ruleId)
            }
        }
    }

    // buildRuleInsert (shared/model/AutomationRuleInsert.kt) is the only path from a draft to
    // an insert payload -- it enforces the no-money-token write-boundary backstop and is
    // tested directly there, so this call is the exact code path production runs, not a
    // stand-in for it.
    suspend fun create(orgId: String, rule: NewAutomationRule): AutomationRule {
        val insert = buildRuleInsert(orgId, uuidV7(), rule)
        return client.from(AUTOMATION_RULES_TABLE)
            .insert(insert) { select() }
            .decodeSingle()
    }

    /**
     * Dry-run over the last 30 days of `activity_events`. `gift.recorded` never carries an
     * amount in its payload (docs/schema.md: amounts stay behind the gated `donations` table),
     * so a min-amount condition is applied by joining each matching event to its donation row --
     * exactly the state-table join the real evaluate-rules job performs, not a payload read.
     */
    suspend fun previewFireCount(orgId: String, triggerVerb: String, minAmountCents: Long?): RulePreviewCount {
        val since = Clock.System.now() - PREVIEW_WINDOW
        val events = client.from(ACTIVITY_EVENTS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("verb", triggerVerb)
                    gte("occurred_at", since.toString())
                }
            }
            .decodeList<EventRow>()
        if (minAmountCents == null) return RulePreviewCount(events.size, complete = true)
        val donationIds = events.mapNotNull { it.payload["donation_id"]?.let(::jsonPrimitiveOrNull) }.distinct()
        if (donationIds.isEmpty()) return RulePreviewCount(0, complete = true)
        val threshold = minAmountCents / 100.0
        val donations = client.from(DONATIONS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    isIn("id", donationIds)
                }
            }
            .decodeList<DonationAmountRow>()
        // Fewer donation rows came back than events named -- RLS (has_perm_strict) hid some
        // from this viewer, so the count below is a lower bound, not a confident answer.
        val complete = donations.size >= donationIds.size
        return RulePreviewCount(donations.count { it.amount >= threshold }, complete)
    }
}

private fun jsonPrimitiveOrNull(element: kotlinx.serialization.json.JsonElement): String? =
    (element as? kotlinx.serialization.json.JsonPrimitive)?.takeIf { !it.isString || it.content.isNotBlank() }?.content

@Serializable
private data class RuleEnabledUpdate(
    @SerialName("is_enabled")
    val isEnabled: Boolean,
    @SerialName("updated_at")
    val updatedAt: String,
)

@Serializable
private data class EventRow(
    val payload: kotlinx.serialization.json.JsonObject,
)

@Serializable
private data class DonationAmountRow(
    val id: String,
    val amount: Double,
)

private val PREVIEW_WINDOW = 30.days
private const val AUTOMATION_RULES_TABLE = "automation_rules"
private const val ACTIVITY_EVENTS_TABLE = "activity_events"
private const val DONATIONS_TABLE = "donations"
